import type { CepResponse } from '@/types/cep';
import type {
  PaginatedTransactions,
  TransactionFilters,
  Transaction,
  AdminTransferDto,
  BulkPaymentDto,
  BulkPaymentResponse,
} from '@/types/transactions';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Transactions Service
 * Handles all transaction-related API endpoints from /api/v1/transactions/*
 */

export const transactionsService = {
  /**
   * Get transactions with filters
   */
  async getTransactions(filters?: TransactionFilters): Promise<PaginatedTransactions> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const queryParams = new URLSearchParams();

    if (filters) {
      if (filters.user_id) queryParams.append('user_id', filters.user_id);
      if (filters.organization_id)
        queryParams.append('organization_id', filters.organization_id);
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.side) queryParams.append('side', filters.side);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
    }

    const queryString = queryParams.toString();
    const url = `/api/v1/transactions${queryString ? `?${queryString}` : ''}`;

    return authenticatedRequest<PaginatedTransactions>(url, {}, token);
  },

  /**
   * Admin: Process a single transfer
   */
  async adminTransfer(data: AdminTransferDto): Promise<Transaction> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Transaction>(
      '/api/v1/admin/transactions/transfer',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Admin: Process bulk payments
   */
  async bulkPayment(data: BulkPaymentDto): Promise<BulkPaymentResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<BulkPaymentResponse>(
      '/api/v1/admin/transactions/bulk',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Get CEP (Comprobante Electrónico de Pago) for a transaction
   */
  async getCep(transactionId: string): Promise<CepResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<CepResponse>(
      `/api/v1/transactions/${transactionId}/cep`,
      { method: 'GET' },
      token
    );
  },
};
